import api from './api';

export interface SubscriptionPackage {
  id: number;
  name: string;
  price: number|string;
  original_price?: number;
  validity_days: number;
  only_for_renewal?: boolean;
  stripe_price_id?: string|null;
  features?: PlanFeature[]|null;
  requires_payment?: boolean|null;
  career_coaching_quota?: number;
  professional_network_building_quota?: number;
}

export interface UserSubscription {
  id: number;
  payment_id: number;
  stripe_subscription_id?: string;
  start_date: string;
  expire_date: string;
  subscribed_at: string;
  is_active: boolean;
  days_remaining: number;
  cycles_remaining: number;
  has_advance_payment: boolean;
  advance_cycles: number;
  package: SubscriptionPackage;
  payment: {
    amount: number;
    bill_id: string;
    paid_at: string;
    status: string;
  };
}

export interface SubscriptionsResponse {
  subscriptions: UserSubscription[];
  active_count: number;
}

export interface PlanFeature {
  text: string;
  available: boolean;
}

export const subscriptionService = {
  // Get user's active subscriptions
  getUserSubscriptions: async (): Promise<SubscriptionsResponse> => {
    const response = await api.get('/v1/stripe/subscriptions');
    const responseData = response.data as any;
    
    // Handle wrapped response structure: { status, message, data }
    if (responseData?.data && responseData?.status === 'success') {
      return responseData.data as SubscriptionsResponse;
    }
    
    // Fallback to direct response (for backward compatibility)
    return responseData as SubscriptionsResponse;
  },

  // Get available subscription packages
  getPackages: async (): Promise<{ packages: SubscriptionPackage[] }> => {
    const response = await api.get('/v1/stripe/packages');
    const responseData = response.data as any;
    
    // Handle wrapped response structure
    if (responseData?.data && responseData?.status === 'success') {
      return responseData.data as { packages: SubscriptionPackage[] };
    }
    
    return responseData as { packages: SubscriptionPackage[] };
  },

  // Cancel subscription
  cancelSubscription: async (subscriptionId: string): Promise<void> => {
    await api.request({
      method: 'DELETE',
      url: '/v1/stripe/subscription',
      data: { subscription_id: subscriptionId }
    });
  },

  // Create subscription checkout session
  createSubscriptionCheckout: async (data: {
    price_id: string;
    subscription_package_id?: number;
    allow_multiple?: boolean;
    source?: string;
    return_url?: string;
  }): Promise<{
    checkout_url: string;
    session_id: string;
    package: {
      id: number;
      name: string;
      price: number;
      validity_days: number;
    };
  }> => {
    const response = await api.post('/v1/stripe/subscription-checkout', data);
    const responseData = response.data as any;
    
    // Handle wrapped response structure
    if (responseData?.data && responseData?.status === 'success') {
      return responseData.data;
    }
    
    return responseData;
  },

  // Toggle auto-renewal
  toggleAutoRenewal: async (data: {
    enable: boolean;
    subscription_id?: string;
    payment_id?: number;
  }): Promise<{
    subscription_id: string;
    auto_renewal: boolean;
    cancel_at_period_end: boolean;
    current_period_end: number;
  }> => {
    const response = await api.post('/v1/stripe/auto-renewal', data);
    const responseData = response.data as any;
    
    if (responseData?.data && responseData?.status === 'success') {
      return responseData.data;
    }
    
    return responseData;
  },

  // Activate free trial subscription directly (without Stripe checkout)
  activateFreeTrial: async (data: {
    subscription_package_id: number;
  }): Promise<{
    status: string;
    message: string;
    data: {
      subscription: {
        id: number;
        package_name: string;
        start_date: string;
        expire_date: string;
        days_remaining: number;
      };
    };
  }> => {
    const response = await api.post('/v1/stripe/activate-free-trial', data);
    const responseData = response.data as any;
    
    // Handle wrapped response structure
    if (responseData?.data && responseData?.status === 'success') {
      return responseData.data;
    }
    
    return responseData;
  },
};

